/**
 * i18n dos primitivos: config global hidratada pelo app antes das telas
 * renderizarem. O texto-fonte do código é pt-BR; `t()` traduz via dicionário
 * quando o idioma da pessoa é outro e devolve o original quando não há
 * tradução (nunca quebra a UI).
 *
 * TRÊS LÍNGUAS, CINCO VARIANTES (founder 25/10). Português (pt-BR), Español
 * (es-419 e es-ES) e English (en-US e en-GB). Cada língua tem UM dicionário
 * completo ("en", "es"); a variante regional só tem um dicionário pequeno de
 * override com as palavras que mudam ("en-GB": colour, organisation; "es-ES":
 * ordenador, móvil). A busca é: override da variante → dicionário da língua →
 * texto-fonte em pt-BR.
 *
 * ⚠️ Os dicionários NÃO moram aqui, e isso é deliberado. Com o pacote sendo
 * consumido também pelo `apps/pdv` (terminal de balcão com orçamento de
 * 200 KB gz), importar inglês e espanhol aqui seria carregar tudo pra nunca
 * usar. Então o registro é invertido: quem tem dicionário chama
 * `registerI18nDict`. O `apps/web` registra no seu shim de `@/lib/i18n`.
 */
type Dict = Record<string, string>;
/* Dicionário pronto ou a fábrica dele. O override regional é DERIVADO do
   dicionário da língua (ver `apps/web/src/i18n/regional.ts`), e calcular isso
   no carregamento custaria pra todo mundo, inclusive pra quem usa português.
   A fábrica só roda quando alguém escolhe aquela variante. */
const DICTS: Record<string, Dict | (() => Dict)> = {};

function resolver(chave: string): Dict | null {
  const d = DICTS[chave];
  if (!d) return null;
  if (typeof d === "function") {
    const pronto = d();
    DICTS[chave] = pronto;
    return pronto;
  }
  return d;
}

let dict: Record<string, string> | null = null;
let override: Record<string, string> | null = null;
let currentLocale = "pt-BR";

/**
 * Códigos antigos que seguem sendo aceitos na LEITURA (orgs e pessoas que
 * escolheram antes de 25/10). Português de Portugal virou português do
 * Brasil, e o espanhol do México e da Argentina virou o latino-americano.
 */
const LEGADO: Record<string, string> = {
  "pt-pt": "pt-BR",
  pt: "pt-BR",
  "pt-br": "pt-BR",
  "es-mx": "es-419",
  "es-ar": "es-419",
  "es-419": "es-419",
  es: "es-419",
  "es-es": "es-ES",
  en: "en-US",
  "en-us": "en-US",
  "en-gb": "en-GB",
};

/**
 * Normaliza qualquer código de idioma pra uma das cinco variantes. Vazio ou
 * desconhecido vira pt-BR (a língua do código-fonte): nunca quebra a UI.
 */
export function normalizeLocale(locale?: string | null): string {
  if (!locale) return "pt-BR";
  const k = locale.trim().replace("_", "-").toLowerCase();
  if (LEGADO[k]) return LEGADO[k];
  const lang = k.slice(0, 2);
  if (lang === "en") return "en-US";
  if (lang === "es") return "es-419";
  return "pt-BR";
}

/**
 * Registra um dicionário. A chave é a língua ("en", "es") para o dicionário
 * completo, ou a variante ("en-GB", "es-ES") para o override regional.
 */
export function registerI18nDict(lang: string, entries: Dict | (() => Dict)): void {
  const chave = lang.length > 2 ? normalizeLocale(lang) : lang.toLowerCase();
  DICTS[chave] = entries;
  /* Registro depois do setI18nConfig (import tardio do dicionário) precisa
     reapontar o dict ativo, senão a língua já escolhida ficaria sem tradução. */
  aplicar();
}

function aplicar(): void {
  const lang = currentLocale.slice(0, 2).toLowerCase();
  dict = lang === "pt" ? null : resolver(lang);
  override = lang === "pt" ? null : resolver(currentLocale);
}

export function setI18nConfig(locale?: string | null): void {
  currentLocale = normalizeLocale(locale);
  aplicar();
}

/**
 * Traduz um texto-fonte (pt-BR) pro idioma da pessoa; sem tradução = original.
 *
 * `contexto` desfaz a ambiguidade quando a MESMA palavra em pt tem dois
 * sentidos ("Nota" é nota fiscal no Financeiro e anotação na agenda). A
 * chave `contexto|texto` ganha; sem ela, vale a tradução geral do texto.
 */
export function t(source: string, contexto?: string): string {
  if (contexto) {
    const k = `${contexto}|${source}`;
    const v = override?.[k] ?? dict?.[k];
    if (v !== undefined) return v;
  }
  return override?.[source] ?? dict?.[source] ?? source;
}

/**
 * `t()` com valores: o texto-fonte leva `{nome}` no lugar do dado, e o
 * dicionário traduz a frase inteira com os mesmos marcadores.
 * `tf("Ações de {nome}", { nome })` → "Actions for Ana".
 * Template string dentro de `t()` (`t(\`Ações de ${nome}\`)`) nunca acha
 * tradução, porque a chave muda a cada dado.
 */
export function tf(source: string, vars: Record<string, string | number>): string {
  return t(source).replace(/\{(\w+)\}/g, (m, k: string) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m,
  );
}

/** A variante ativa, já normalizada (pt-BR · es-419 · es-ES · en-US · en-GB). */
export function i18nLocale(): string {
  return currentLocale;
}

/**
 * Diz se `label` é o texto-fonte pt-BR OU uma tradução conhecida dele em
 * qualquer dicionário registrado. Usado na nav pra não "congelar" um label
 * traduzido (ex.: "Users") quando a pessoa volta pro português.
 */
export function isLocaleVariantOf(sourcePt: string, label: string): boolean {
  if (label === sourcePt) return true;
  for (const chave of Object.keys(DICTS)) {
    const d = DICTS[chave];
    /* Fábrica ainda não rodou = variante que ninguém escolheu nesta sessão;
       a tradução da língua base já cobre o rótulo. */
    if (typeof d === "function") continue;
    if (d?.[sourcePt] === label) return true;
  }
  return false;
}
